import { ArrowRight, Download } from "lucide-react";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HeroSection() {
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", school_name: "", designation: "", mobile: "", email: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.mobile) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/brochure-download`, form);
      toast.success("Brochure download started!");
      setShowForm(false);
      setForm({ name: "", school_name: "", designation: "", mobile: "", email: "" });
      window.open(process.env.REACT_APP_BROCHURE_PDF_URL, "_blank");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img src={process.env.REACT_APP_HERO_BG_URL} alt="Open Sky Adventure Academy" className="w-full h-full object-cover" loading="eager" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0D1B2A]/80 via-[#0D1B2A]/55 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0D1B2A]/60 via-transparent to-[#0D1B2A]/20" />
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-24 w-full">
        <div className="max-w-3xl">
          <p className="text-xs tracking-[0.25em] uppercase font-semibold text-white/60 mb-6 animate-fade-in-up">Experiential Learning &middot; Agriculture &middot; Aviation &middot; Sailing</p>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-medium text-white leading-[1.05] tracking-tighter mb-6 animate-fade-in-up delay-100">
            Beyond the Classroom.<br /><span className="text-accent">Beyond the Ordinary.</span>
          </h1>
          <p className="text-base sm:text-lg text-white font-light leading-relaxed max-w-xl mb-10 animate-fade-in-up delay-200">
            Empowering the next generation of Indian innovators through immersive, curriculum-aligned experiences in Agriculture, Aviation, and Maritime Science-a short drive from Bengaluru.
          </p>
          <div className="flex flex-wrap gap-4 animate-fade-in-up delay-300">
            <button data-testid="hero-cta-brochure" onClick={() => setShowForm(true)} className="inline-flex items-center gap-2.5 px-7 py-3.5 bg-accent text-accent-foreground font-medium text-sm rounded-sm hover:bg-accent/90 transition-all hover:-translate-y-0.5 hover:shadow-lg">
              <Download className="w-4 h-4" /> Download Brochure
            </button>
            <a href="#programs" data-testid="hero-cta-programs" onClick={(e) => { e.preventDefault(); document.querySelector("#programs")?.scrollIntoView({ behavior: "smooth" }); }} className="inline-flex items-center gap-2.5 px-7 py-3.5 text-white font-medium text-sm rounded-sm border border-white/25 hover:bg-white/10 transition-all hover:-translate-y-0.5 backdrop-blur-sm">
              Explore Programs
            </a>
          </div>
          <div className="flex flex-wrap gap-8 mt-16 animate-fade-in-up delay-400 bg-[#0D1B2A]/70 backdrop-blur-sm rounded-sm px-8 py-6 w-fit">
            {[{ value: "7.5", label: "Acre Campus" }, { value: "92%", label: "School Return Rate" }, { value: "K-12", label: "All Grades Served" }].map((stat) => (
              <div key={stat.label}>
                <div className="font-heading text-3xl md:text-4xl font-semibold text-white">{stat.value}</div>
                <div className="text-xs tracking-wider uppercase text-white/60 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-sm p-8 max-w-md w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="brochure-modal">
            <h3 className="font-heading text-2xl font-medium text-foreground mb-2">Download Brochure</h3>
            <p className="text-sm text-muted-foreground mb-6">Fill in your details and we'll send you our prospectus.</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground mb-1 block">Full Name *</label>
                <Input placeholder="Your name" value={form.name} onChange={(e) => { const v = e.target.value.replace(/[^a-zA-Z\s]/g, ''); setForm(p => ({...p, name: v})); }} className="border-border/60" data-testid="brochure-name" />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-1 block">School / Institution</label>
                <Input placeholder="School name" value={form.school_name} onChange={(e) => { const v = e.target.value.replace(/[^a-zA-Z0-9\s.'-]/g, ''); setForm(p => ({...p, school_name: v})); }} className="border-border/60" data-testid="brochure-school" />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-1 block">Designation</label>
                <Input placeholder="e.g. Principal, Trustee, Coordinator" value={form.designation} onChange={(e) => setForm(p => ({...p, designation: e.target.value}))} className="border-border/60" data-testid="brochure-designation" />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-1 block">Mobile Number *</label>
                <Input type="tel" placeholder="+91 XXXXX XXXXX" value={form.mobile} onChange={(e) => { const v = e.target.value.replace(/[^0-9+\s-]/g, ''); setForm(p => ({...p, mobile: v})); }} className="border-border/60" data-testid="brochure-mobile" />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-1 block">Email Address *</label>
                <Input type="email" placeholder="email@school.com" value={form.email} onChange={(e) => setForm(p => ({...p, email: e.target.value}))} className="border-border/60" data-testid="brochure-email" required />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={loading} data-testid="brochure-submit" className="flex-1 px-6 py-3 bg-accent text-accent-foreground font-medium text-sm rounded-sm hover:bg-accent/90 transition-all disabled:opacity-50">
                  {loading ? "Sending..." : "Download Now"}
                </button>
                <button type="button" onClick={() => setShowForm(false)} className="px-6 py-3 border border-border/60 text-foreground font-medium text-sm rounded-sm hover:bg-muted transition-all">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
